import {
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
} from '@chakra-ui/menu'
import {
  Box,
  Divider,
  Flex,
  Icon,
  IconButton,
  Spacer,
  Spinner,
  Table,
  Tbody,
  Td,
  Text as ChakraText,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import { FiEdit } from 'react-icons/fi'
import { RiDeleteBinFill } from 'react-icons/ri'
import type { CellProps, Column } from 'react-table'
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'
import { colors } from '../../../ui-kit'
import { PermissionsEnum } from '../../../utils'
import type { UserGroupResponse, UserSessionResponse } from '../../interfaces'
import Pagination from '../Pagination'
import PermissionGuard from '../PermissionGuard'
import SearchBar from '../SearchBar'
import CreateUpdateUserGroupModal from './CreateUpdateUserGroupModal'
import DeleteUserGroupModal from './DeleteUserGroupModal'

interface Props {
  user: UserSessionResponse | null
  data: UserGroupResponse[] | undefined
  loading: boolean
  getData(): Promise<void>
}

interface userGroupData {
  id: string
  label: string
  userIds?: string[]
}

const UserGroupTable = ({
  user,
  data,
  getData,
  loading,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const [userGroupData, setUserGroupData] = useState<userGroupData>()

  const {
    isOpen: isOpenEditUser,
    onOpen: onOpenEditUser,
    onClose: onCloseEditUser,
  } = useDisclosure()

  const {
    isOpen: isOpenDeleteUserGroup,
    onOpen: onOpenDeleteUserGroup,
    onClose: onCloseDeleteUserGroup,
  } = useDisclosure()

  useEffect(() => {
    void getData()
  }, [user])

  const tableData = useMemo<UserGroupResponse[]>(() => data ?? [], [data])

  const columns = useMemo<Array<Column<UserGroupResponse>>>(
    () => [
      {
        Header: t('groups', 'Groups').toString(),
        accessor: values => values.label,
      },
      {
        Header: t('members', 'Members').toString(),
        accessor: values => values.userIds.length,
      },
      {
        Header: t('created_on', 'Created on').toString(),
        accessor: values =>
          moment(values.createdAt).format('DD MMMM YYYY, hh:mm A'),
        Cell: ({ value, row }: CellProps<UserGroupResponse, string>) => (
          <Flex justify="space-between">
            <Box>{value}</Box>
            <PermissionGuard permission={PermissionsEnum.SELF_TEAM_MANAGE}>
              {(hasPermission: boolean) =>
                hasPermission ? (
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="Options"
                      icon={<BsThreeDotsVertical />}
                      variant="link"
                      boxShadow="none"
                    />
                    <MenuList>
                      <MenuItem
                        icon={<FiEdit />}
                        onClick={() => {
                          setUserGroupData({
                            id: row.original.groupId,
                            label: row.original.label,
                            userIds: row.original.userIds,
                          })
                          onOpenEditUser()
                        }}
                      >
                        {t('view_edit_user_group', 'View/update user group')}
                      </MenuItem>
                      <MenuDivider />
                      <MenuItem
                        icon={<RiDeleteBinFill />}
                        color={colors.appBrick}
                        onClick={() => {
                          setUserGroupData({
                            id: row.original.groupId,
                            label: row.original.label,
                          })
                          onOpenDeleteUserGroup()
                        }}
                      >
                        {t('delete', 'Delete')}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                ) : (
                  <></>
                )
              }
            </PermissionGuard>
          </Flex>
        ),
      },
    ],
    [],
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    state: { pageIndex, pageSize, ...tableState },
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    setGlobalFilter,
    setPageSize,
    previousPage,
    nextPage,
    gotoPage,
  } = useTable<UserGroupResponse>(
    { columns, data: tableData },
    useGlobalFilter,
    useSortBy,
    usePagination,
  )

  const pageOffset = pageIndex * pageSize

  return (
    <>
      <Flex align="center">
        <SearchBar
          searchQuery={tableState.globalFilter as string}
          setSearchQuery={setGlobalFilter}
          padding="1.5em 0em"
          minWidth="30em"
        />
        <Spacer />
        {rows.length ? (
          <ChakraText fontWeight="500">
            {t('showing', 'Showing')} {pageOffset + (pageCount === 0 ? 0 : 1)}{' '}
            {t('to', 'to')}{' '}
            {pageOffset + pageSize <= rows.length
              ? pageOffset + pageSize
              : rows.length}{' '}
            {t('of', 'of')} {rows.length} {t('entries', 'entries')}
          </ChakraText>
        ) : null}
      </Flex>
      <Table {...getTableProps}>
        <Thead bgColor="appPaleGreyTwo">
          {headerGroups.map(headerGroup => {
            const { key, ...restHeaderGroupProps } =
              headerGroup.getHeaderGroupProps()
            return (
              <Tr key={key} {...restHeaderGroupProps}>
                {headerGroup.headers.map(column => {
                  const { key: keyCol, ...restColumn } = column.getHeaderProps(
                    column.getSortByToggleProps(),
                  )
                  return (
                    <Th key={keyCol} {...restColumn}>
                      {column.render('Header')}
                      {!column.disableSortBy ? (
                        <Box as="span" pl="2" color={colors.appBattleshipGrey}>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon
                                as={FaSortUp}
                                aria-label={t(
                                  'sorted_descending',
                                  'sorted descending',
                                )}
                              />
                            ) : (
                              <Icon
                                as={FaSortDown}
                                aria-label={t(
                                  'sorted_ascending',
                                  'sorted ascending',
                                )}
                              />
                            )
                          ) : (
                            <Icon
                              as={FaSort}
                              aria-label={t('sort_by_column', 'sort by column')}
                            />
                          )}
                        </Box>
                      ) : null}
                    </Th>
                  )
                })}
              </Tr>
            )
          })}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {!loading ? (
            rows.length ? (
              page.map(row => {
                prepareRow(row)
                const { key, ...restRowProps } = row.getRowProps()
                return (
                  <Tr key={key} {...restRowProps}>
                    {row.cells.map(cell => {
                      const { key: keyRow, ...restCellProps } =
                        cell.getCellProps()
                      return (
                        <Td key={keyRow} {...restCellProps}>
                          <ChakraText>{cell.render('Cell')}</ChakraText>
                        </Td>
                      )
                    })}
                  </Tr>
                )
              })
            ) : (
              <Tr>
                <Td colSpan={4} textAlign="center">
                  <ChakraText>
                    {t('no_groups_created', 'No groups created')}
                  </ChakraText>
                </Td>
              </Tr>
            )
          ) : (
            <Tr>
              <Td colSpan={4} textAlign="center">
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="appPaleGrey"
                  color="appBlue"
                  size="xl"
                />
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
      {pageOptions.length > 0 ? <Divider margin="3em 0em 1em" /> : null}
      <Flex>
        <Spacer />
        <Pagination
          pageIndex={pageIndex}
          pageSize={pageSize}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          previousPage={previousPage}
          nextPage={nextPage}
          gotoPage={gotoPage}
        />
      </Flex>
      <CreateUpdateUserGroupModal
        isUpdate={true}
        {...userGroupData}
        user={user}
        isOpen={isOpenEditUser}
        onClose={onCloseEditUser}
        getData={getData}
      />
      <DeleteUserGroupModal
        user={user}
        {...userGroupData}
        isOpen={isOpenDeleteUserGroup}
        onClose={onCloseDeleteUserGroup}
        getData={getData}
      />
    </>
  )
}

export default UserGroupTable
